import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  DEFAULT_MOTION_REVIEW_NOTES_DISCLAIMER,
  type MotionReviewNotesPayload,
} from '../../lib/reportApi';

export interface MotionReviewNotesSectionProps {
  payload: MotionReviewNotesPayload;
  initialOpen?: boolean;
}

export function hasRenderableMotionReviewNotes(
  payload: MotionReviewNotesPayload | null | undefined
): boolean {
  return Boolean(payload && payload.items.length > 0);
}

export const MotionReviewNotesSection: React.FC<MotionReviewNotesSectionProps> = ({
  payload,
  initialOpen = false,
}) => {
  const [open, setOpen] = useState(initialOpen);
  const title = payload.title?.trim() || 'Motion Review Notes';
  const disclaimer = payload.disclaimer?.trim() || DEFAULT_MOTION_REVIEW_NOTES_DISCLAIMER;

  return (
    <section
      className="card"
      data-testid="motion-review-notes-section"
    >
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between text-left"
        data-testid="motion-review-notes-toggle"
      >
        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">{title}</h3>
        {open ? (
          <ChevronUp className="h-5 w-5 text-secondary-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-secondary-500" />
        )}
      </button>

      {open ? (
        <div className="mt-4 space-y-4">
          <p
            className="text-xs text-secondary-600 dark:text-secondary-300"
            data-testid="motion-review-notes-disclaimer"
          >
            {disclaimer}
          </p>
          <div className="space-y-3">
            {payload.items.map((item, index) => (
              <article
                key={`motion-note-${index}`}
                className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-700"
                data-testid={`motion-review-notes-item-${index}`}
              >
                <p
                  className="text-sm text-secondary-900 dark:text-white"
                  data-testid={`motion-review-notes-statement-${index}`}
                >
                  {item.statement}
                </p>
                {item.concept_explanation ? (
                  <div
                    className="mt-2"
                    data-testid={`motion-review-notes-explanation-${index}`}
                  >
                    <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400">
                      What it means
                    </p>
                    <p className="mt-1 text-xs text-secondary-600 dark:text-secondary-300">
                      {item.concept_explanation}
                    </p>
                  </div>
                ) : null}
                <dl className="mt-2 grid grid-cols-1 gap-1 text-xs text-secondary-600 dark:text-secondary-300 sm:grid-cols-2">
                  <div>
                    <dt className="font-medium text-secondary-500 dark:text-secondary-400">
                      Concept
                    </dt>
                    <dd data-testid={`motion-review-notes-concept-${index}`}>
                      {item.concept_display}
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-secondary-500 dark:text-secondary-400">
                      Area
                    </dt>
                    <dd data-testid={`motion-review-notes-area-${index}`}>
                      {item.attack_area_display}
                    </dd>
                  </div>
                </dl>
              </article>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default MotionReviewNotesSection;
